import { useState, useRef, useEffect, useCallback } from 'react';
import { Send } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { cn } from '@/lib/utils';
import { useAuth } from '@/contexts/AuthContext';
import { supabase } from '@/lib/supabase';
import type { Message, TabId, UpcomingOrderStatus } from './chat.types';
import { isOrderIntent } from './faqMatcher';

const GREETING: Message = {
  id: 'greeting',
  role: 'bot',
  text: "Hi! I'm the Protein Meals assistant. What can I help you with today?",
  chips: ['Pricing', 'Delivery area', 'My order', 'Talk to a person'],
  timestamp: new Date(),
};

const FALLBACK_TEXT =
  "I'm not sure about that one! Our team would love to help — reach us on WhatsApp or Messenger for a quick response.";

function getCurrentWeekStart(): string {
  const today = new Date();
  const day = today.getDay();
  const diff = day === 0 ? -6 : 1 - day;
  const monday = new Date(today);
  monday.setDate(today.getDate() + diff);
  return monday.toISOString().split('T')[0];
}

interface BotTabProps {
  onSwitchTab: (tab: TabId) => void;
}

export function BotTab({ onSwitchTab }: BotTabProps) {
  const { user } = useAuth();
  const [messages, setMessages] = useState<Message[]>([GREETING]);
  const [input, setInput] = useState('');
  const [typing, setTyping] = useState(false);
  const [isOffline, setIsOffline] = useState(!navigator.onLine);
  const bottomRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const onOnline = () => setIsOffline(false);
    const onOffline = () => setIsOffline(true);
    window.addEventListener('online', onOnline);
    window.addEventListener('offline', onOffline);
    return () => {
      window.removeEventListener('online', onOnline);
      window.removeEventListener('offline', onOffline);
    };
  }, []);

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages, typing]);

  const addBotMsg = useCallback((text: string, chips?: string[]) => {
    setMessages(prev =>
      prev.concat({ id: crypto.randomUUID(), role: 'bot', text, chips, timestamp: new Date() }),
    );
  }, []);

  const handleSend = useCallback(
    async (text: string) => {
      if (!text.trim() || typing) return;

      setMessages(prev =>
        prev
          .map(m => ({ ...m, chips: undefined }))
          .concat({ id: crypto.randomUUID(), role: 'user', text: text.trim(), chips: undefined, timestamp: new Date() }),
      );
      setInput('');
      setTyping(true);
      await new Promise(r => setTimeout(r, 600));
      setTyping(false);

      if (text === 'Talk to a person' || text === 'WhatsApp') {
        onSwitchTab('whatsapp');
        return;
      }
      if (text === 'Messenger') {
        onSwitchTab('messenger');
        return;
      }

      if (isOrderIntent(text)) {
        if (!user) {
          addBotMsg('Log in to your account to check your order status.', ['Anything else?', 'Talk to a person']);
          return;
        }
        try {
          const { data, error } = await supabase.rpc('get_upcoming_order_status', {
            p_week_start_date: getCurrentWeekStart(),
          });
          if (error) throw error;
          const status = data as UpcomingOrderStatus;
          if (status?.order) {
            addBotMsg(
              `Your order for the week of ${status.week_start_date ?? 'this week'} is currently ${status.order.status}. Check your Account page for full details.`,
              ['Anything else?', 'Talk to a person'],
            );
          } else if (status?.pending_assignment) {
            addBotMsg(
              'Your order for this week is being prepared — meals will be assigned soon! Check your Account page for updates.',
              ['Anything else?'],
            );
          } else if (status?.subscription_status === 'paused') {
            addBotMsg(
              "Your subscription is currently paused, so there's no upcoming order. You can resume from your Account page.",
              ['Anything else?', 'Talk to a person'],
            );
          } else {
            addBotMsg(
              "I couldn't find an upcoming order for your account. Check your Account page for details.",
              ['Anything else?', 'Talk to a person'],
            );
          }
        } catch {
          addBotMsg(
            "I couldn't fetch your order right now. Please check your Account page directly.",
            ['Anything else?', 'Talk to a person'],
          );
        }
        return;
      }

      try {
        const history = messages
          .filter(m => m.id !== 'greeting')
          .slice(-8)
          .map(m => ({ role: m.role === 'bot' ? 'assistant' as const : 'user' as const, content: m.text }));

        const { data, error } = await supabase.functions.invoke('chat-bot', {
          body: { message: text, history, userId: user?.id ?? null },
        });

        addBotMsg(
          (!error && data?.reply) ? data.reply : FALLBACK_TEXT,
          ['Anything else?', 'Talk to a person'],
        );
      } catch {
        addBotMsg(FALLBACK_TEXT, ['WhatsApp', 'Messenger']);
      }
    },
    [typing, user, messages, onSwitchTab, addBotMsg],
  );

  return (
    <div className="flex flex-col h-full">
      <div className="flex-1 overflow-y-auto p-3 space-y-3 min-h-0">
        <AnimatePresence initial={false}>
          {messages.map(msg => (
            <motion.div
              key={msg.id}
              initial={{ opacity: 0, y: 8 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.2 }}
              className={cn('flex gap-2', msg.role === 'user' && 'justify-end')}
            >
              {msg.role === 'bot' && (
                <div className="w-7 h-7 rounded-full bg-[#E8A838] flex items-center justify-center shrink-0 text-white text-[10px] font-bold leading-none">
                  PM
                </div>
              )}
              <div className="max-w-[78%] space-y-1.5">
                <div
                  className={cn(
                    'px-3 py-2 text-sm leading-relaxed whitespace-pre-wrap',
                    msg.role === 'bot'
                      ? 'bg-gray-100 text-gray-900 rounded-2xl rounded-tl-sm'
                      : 'bg-[#E8A838] text-white rounded-2xl rounded-tr-sm',
                  )}
                >
                  {msg.text}
                </div>
                {msg.chips && msg.chips.length > 0 && (
                  <div className="flex flex-wrap gap-1.5">
                    {msg.chips.map(chip => (
                      <button
                        key={chip}
                        onClick={() => void handleSend(chip)}
                        className="px-3 py-1 text-xs rounded-full border border-[#E8A838] text-[#E8A838] hover:bg-amber-50 transition-colors"
                      >
                        {chip}
                      </button>
                    ))}
                  </div>
                )}
              </div>
            </motion.div>
          ))}
        </AnimatePresence>

        {typing && (
          <div className="flex gap-2 items-center">
            <div className="w-7 h-7 rounded-full bg-[#E8A838] flex items-center justify-center shrink-0 text-white text-[10px] font-bold leading-none">
              PM
            </div>
            <div className="bg-gray-100 rounded-2xl rounded-tl-sm px-3 py-2.5 flex gap-1 items-center">
              {[0, 1, 2].map(i => (
                <motion.span
                  key={i}
                  className="w-1.5 h-1.5 bg-gray-400 rounded-full block"
                  animate={{ y: [0, -4, 0] }}
                  transition={{ duration: 0.6, repeat: Infinity, delay: i * 0.15 }}
                />
              ))}
            </div>
          </div>
        )}

        <div ref={bottomRef} />
      </div>

      <div className="p-3 border-t border-gray-100 shrink-0">
        {isOffline && (
          <p className="text-xs text-red-500 mb-2 text-center">You appear to be offline.</p>
        )}
        <div className="flex gap-2">
          <input
            value={input}
            onChange={e => setInput(e.target.value)}
            onKeyDown={e => e.key === 'Enter' && !e.shiftKey && void handleSend(input)}
            placeholder="Type a message..."
            disabled={isOffline || typing}
            className="flex-1 text-sm px-3 py-2 rounded-xl border border-gray-200 focus:outline-none focus:border-[#E8A838] disabled:opacity-50 bg-white"
          />
          <button
            onClick={() => void handleSend(input)}
            disabled={!input.trim() || isOffline || typing}
            className="w-9 h-9 shrink-0 rounded-xl bg-[#E8A838] text-white flex items-center justify-center disabled:opacity-40 hover:bg-amber-500 transition-colors"
          >
            <Send className="w-4 h-4" />
          </button>
        </div>
      </div>
    </div>
  );
}
